import { useCallback, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AddFactDialog } from './dialogs/add-fact-dialog';
import { AddToChronologyDialog } from './dialogs/add-to-chronology-dialog';
import { BatchEditDialog } from './dialogs/batch-edit-dialog';
import { FilterFactsDialog } from './dialogs/filter-facts-dialog';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateChronology } from '@/api/mutations/useCreateChronology';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import { useGetCaseThemes } from '@/api/queries/useGetCaseThemes';
import { useGetChronologies } from '@/api/queries/useGetChronologies';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { Person, Run } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { ClusterFactsModal } from '@/new/components/labeller/cluster-facts';
import { CreateInconsistencyModal } from '@/new/components/labeller/create-inconsistency';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { addFactsBatchToVisualisation } from '@/screens/Chronos/CaseEditor/DataView/ChronologyViewer/ChronologyEditor/VisualTimeline/SyncingHelpers/syncVisualisationWithTimelineChanges';
import { FactFilters as FactFiltersType } from '@/types';

interface FactsEditorToolbarProps {
  globalFilter: string;
  resultsIncludedLength: number;
  refetchCaseFacts: () => void;
  clearSearch: () => void;
  setGlobalFilter: (value: string) => void;
  canGetPrevPage: boolean;
  canGetNextPage: boolean;
  prevPage: () => void;
  nextPage: () => void;
  currentPage: number;
  noOfPages: number | undefined;
  onSearchCall: (searchValue?: string, useNaturalLanguage?: boolean) => void;
  goToPage: (value: number) => void;
  activeFilters: FactFiltersType;
  setFilters: (v: FactFiltersType) => void;
  caseRuns: Run[];
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  allPersons: Person[];
  onClusterComplete: () => void;
  processingNL: boolean;
  filterCount: number;
  totalUnfilteredCount: number | undefined;
  totalFilteredCount: number | undefined;
}

export const FactsTableHeader = ({
  globalFilter,
  refetchCaseFacts,
  clearSearch,
  setGlobalFilter,
  resultsIncludedLength,
  canGetPrevPage,
  canGetNextPage,
  prevPage,
  nextPage,
  currentPage,
  noOfPages,
  onSearchCall,
  goToPage,
  activeFilters,
  setFilters,
  caseRuns,
  pageSize,
  onPageSizeChange,
  allPersons,
  onClusterComplete,
  processingNL,
  filterCount,
  totalUnfilteredCount,
  totalFilteredCount,
}: FactsEditorToolbarProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) {
    return;
  }

  const { data: themesWithCounts = [] } = useGetCaseThemes(matterId);
  const { data: docTypes = [] } = useGetCaseDocTypes({ caseId: matterId });

  const caseThemes = themesWithCounts.map((theme) => theme.name);
  // State
  const [factAdderModalIsOpen, setFactAdderModalIsOpen] = useState(false);
  const [factBulkUpdateModalIsOpen, setFactBulkUpdateModalIsOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [selectChronologyModalIsOpen, setSelectChronologyModalIsOpen] = useState(false);
  const [clusterModalOpen, setClusterModalOpen] = useState(false);
  const [createInconsistencyModalOpen, setCreateInconsistencyModalOpen] = useState(false);

  const navigate = useNavigate();
  const authInfo = useAuthInfo();

  const { user } = useUserContext();
  const isLabellingOrg = user?.organisation?.isLabellingOrg ?? false;

  // Data fetching
  const { data: docIdsResponse } = useGetDocIds(matterId);
  const { data: chronologiesResponse, isLoading: isLoadingChronologies } = useGetChronologies({
    caseId: matterId,
    paginated: false,
  });

  const { createChronology } = useCreateChronology({
    onSuccess: (data) => {
      navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/${data.chronologyId}`, { replace: true });
    },
    onError: (error) => {
      toast.error('Failed to create chronology');
    },
  });

  const handleNewFactCreated = useCallback(() => {
    trackCustomEvent(TrackingEvents.AddFact, { matterId });
    refetchCaseFacts();
    setFactAdderModalIsOpen(false);
  }, [refetchCaseFacts, matterId]);

  const handleCreateChronology = async () => {
    await createChronology.mutateAsync({ caseId: matterId, chronologyId: null, type: null });
    trackCustomEvent(TrackingEvents.CreateFactFromFactsPage, { matterId });
  };

  const handleSelectChronology = async (chronologyId: string) => {
    setSelectChronologyModalIsOpen(false);
    try {
      // Add facts to the chronology
      const result = await createChronology.mutateAsync({ caseId: matterId, chronologyId, type: null });

      // If facts were added to an existing chronology, sync the visual timeline
      if (result.addedFacts && result.addedFacts.length > 0) {
        await addFactsBatchToVisualisation({
          caseId: matterId!,
          chronologyId,
          newFacts: result.addedFacts,
          getAccessToken: authInfo.tokens.getAccessToken,
        });
      }

      trackCustomEvent(TrackingEvents.AddToChronologyFromFactsPage, { matterId, chronologyId });
    } catch (error) {
      console.error('Error adding facts to chronology:', error);
      toast.error('Failed to add facts to chronology');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={resultsIncludedLength === 0}>
                <Icon className="lucide--git-fork" />
                Chronology
                <Icon className="lucide--chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom">
              <DropdownMenuItem onClick={handleCreateChronology}>Create New</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectChronologyModalIsOpen(true)}>Add to Existing</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {isLabellingOrg && (
            <>
              <Button disabled={resultsIncludedLength < 2} onClick={() => setClusterModalOpen(true)}>
                <Icon className="lucide--group" />
                Create Inconsistency
              </Button>

              <Button disabled={resultsIncludedLength < 2} onClick={() => setCreateInconsistencyModalOpen(true)}>
                <Icon className="lucide--triangle-alert" />
                Create Inconsistency
              </Button>
            </>
          )}

          <AddFactDialog
            isOpen={factAdderModalIsOpen}
            onOpenChange={setFactAdderModalIsOpen}
            caseId={matterId}
            docs={docIdsResponse?.documents ?? []}
            handleNewFactCreated={handleNewFactCreated}
          />

          <BatchEditDialog
            isOpen={factBulkUpdateModalIsOpen}
            onOpenChange={setFactBulkUpdateModalIsOpen}
            selectedFactCount={resultsIncludedLength}
            caseId={matterId}
          />
        </div>
        <div className="flex items-center gap-2">
          <FilterFactsDialog
            isOpen={filtersModalOpen}
            onOpenChange={setFiltersModalOpen}
            filterCount={filterCount}
            activeFilters={activeFilters}
            allDocumentTypes={docTypes}
            setFilters={setFilters}
            caseRuns={caseRuns}
            caseThemes={caseThemes}
            allPersons={allPersons}
            goToPage={goToPage}
          />

          <Pagination
            canGetPrevPage={canGetPrevPage}
            canGetNextPage={canGetNextPage}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={currentPage}
            noOfPages={noOfPages}
            goToPage={goToPage}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            totalCount={totalUnfilteredCount}
            filteredCount={totalFilteredCount}
            entityName="facts"
          />
        </div>

        <AddToChronologyDialog
          isOpen={selectChronologyModalIsOpen}
          onOpenChange={setSelectChronologyModalIsOpen}
          isLoadingChronologies={isLoadingChronologies}
          chronologies={chronologiesResponse?.chronologies ?? []}
          handleSelectChronology={handleSelectChronology}
        />

        {clusterModalOpen && (
          <ClusterFactsModal
            isOpen={clusterModalOpen}
            onClose={() => setClusterModalOpen(false)}
            caseId={matterId}
            onSuccess={() => {
              setClusterModalOpen(false);
              onClusterComplete();
            }}
          />
        )}
        {createInconsistencyModalOpen && (
          <CreateInconsistencyModal
            isOpen={createInconsistencyModalOpen}
            onClose={() => setCreateInconsistencyModalOpen(false)}
            caseId={matterId}
            onSuccess={() => {
              setCreateInconsistencyModalOpen(false);
              refetchCaseFacts();
            }}
          />
        )}
      </div>

      <div id="facts-search-bar">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="facts"
        />
      </div>
    </div>
  );
};
